import { nameOf } from "../../../../utils/functions/ReflectionUtil";
import {
  Design,
  DesignState,
  getDesignState,
} from "../../../../core/models/entities/Design";
import { formatDate } from "../../../../utils/functions/DateUtil";
import IconButton from "../../../components/LogoButton/IconButton";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheck, faClose } from "@fortawesome/free-solid-svg-icons";
import React, { useRef, useState } from "react";
import { ColumnSettings } from "../../../components/GenericTable/SubComponents/ListFrame";
import { StrucCol } from "../../../components/StructureElements/StrucCol";
import { StrucRow } from "../../../components/StructureElements/StrucRow";
import { DesignPrices } from "../../../components/Logo/LogoPrices";
import { DesignStatusElement } from "../../../components/Design/DesignStatusElement";
import {
  ProductPendingBadge,
  ProductPendingLock,
} from "../../../components/Logo/ProductPendingBadge";
import {
  designProductPendingSinceUtc,
  designActionLockVariant,
  isDesignActionLocked,
} from "../../../../utils/functions/productPending";
import { DesignImage } from "../../../components/Design/DesignImage";
import {
  LIST_PERF_EAGER_ROWS,
  LIST_PERF_IMAGE_BOX,
} from "../../../shared/const/listPerf";
import { enviroment } from "../../../../enviroment";
import {
  isDesignOrderable,
  vendorCanSettleAndOrder,
} from "../../../../utils/functions/orderability";
import { Verdict } from "../../../components/ApprovalVoting/approval-voting";
import { Tooltip } from "@mui/material";
import { useProfileContext } from "../../../../contexts/ProfileContext";
import { useCartHidden } from "../../../../hooks/useCartHidden";
import { Role } from "../../../../core/models/entities/Role";
import { DesignClickCallback } from "../vmHooks/useFunctionalListDesignViewModel";
import {
  AddToCartIcon,
  EditIcon,
  NoteIcon,
  TrashcanCrossIcon,
} from "../../../../resources/icons/Icons";
import { Stepper } from "../../../components/Buttons/Stepper";
import { ApprovalStatus } from "../../../../core/models/entities/Approval";
import CustomerTooltip from "../../../components/Organization/CustomerTooltip";
import {
  getDesignMinQuantity,
  isValidDesignQuantity,
} from "../../../../utils/functions/QuantityUtil";
import { hasUserPendingApprovalStatus } from "../../../../core/models/entities/BaseLogo";
import { usePriceVisibility } from "../../../../contexts/PriceVisibilityContext";
import { CircularProgress } from "@mui/material";
import { ConfirmationGuard } from "../../../components/ConfirmationGuard";
import { CopyNumberButton } from "../../../components/CopyToClipboard/copy-number-button.component";
import { useTranslation } from "react-i18next";
import { rowTextClickProps } from "../../../components/GenericTable/rowTextClick";
import { CLARITY_UNMASK } from "../../../../utils/analytics/clarityAttrs";

interface DesignRowItem {
  design: Design;
  columns: ColumnSettings;
  onApprove: DesignClickCallback;
  onReject: DesignClickCallback;
  onPlaceVerdict: (design: Design, verdict: Verdict) => void;
  onSeeRejectNote: (design: Design) => void;
  onAddToCart: (design: Design, quantity: number) => void;
  onEditDesign: (design: Design) => void;
  onDeleteDesign: (design: Design) => void;
  onInitiateApproval: (design: Design) => void;
  onRejectAndDelete: (design: Design, note?: string) => Promise<void>;
  // Flag REACT_APP_VENDOR_ORDER_WHILE_CUSTOMER_PENDING_ENABLED: the dealer's
  // "Add to order" on a design the customer still has to approve - settles
  // the group (approval-start {skip:true}) and then adds to the cart. The
  // branch is offered only when the caller plumbs this; absent = today's row.
  // The row disables its button while the returned promise runs and swallows
  // the rejection (the handler has already shown its one toast).
  onSettleAndAddToCart?: (design: Design, quantity: number) => Promise<void> | void;
  updatingDesignId?: number;
  loadingPrices?: boolean;
  // Folder drag-and-drop (vendor/admin only): when set, the row becomes an
  // HTML5 drag source; the callback fills the dataTransfer payload (see
  // folder-bar.component.tsx for the drop-target side).
  onDragStartDesign?: (
    design: Design,
    e: React.DragEvent<HTMLDivElement>,
  ) => void;
  // Flag REACT_APP_LIST_PERF_ENABLED: the row's index on the current page
  // (ListFrame's third renderRow argument) - same contract as LogoRowItem
  // (eager rows 0-3, lazy below, reserved 110x74 box). Unused with the
  // flag off = today's DOM.
  rowIndex?: number;
}

// TODO Deprecate
export function isDesignApprovedByMe(design: Design, role: Role) {
  switch (role) {
    case Role.Admin:
      return false;
    case Role.Vendor:
      // Return true only if vendor has explicitly approved
      return design.approval?.vendor?.status === ApprovalStatus.Approved;
    case Role.EndUser:
      // Return true only if customer has explicitly approved
      return design.approval?.customer?.status === ApprovalStatus.Approved;
  }
}

export function DesignRowItem({
  design,
  columns,
  onSeeRejectNote,
  onDeleteDesign,
  onEditDesign,
  onAddToCart,
  onSettleAndAddToCart,
  onReject,
  onApprove,
  updatingDesignId,
  loadingPrices,
  onDragStartDesign,
  rowIndex,
}: DesignRowItem) {
  const listPerf = enviroment.listPerfEnabled && rowIndex !== undefined;
  const { t } = useTranslation();
  const { role } = useProfileContext();
  const { hidePrices, showCustomerPrices } = usePriceVisibility();
  const cartHidden = useCartHidden();

  // Check if design has customer prices
  const hasCustomerPrices =
    design.product?.totalPrices?.some(
      (p: any) =>
        p.adjustedCustomerPrice !== null &&
        p.adjustedCustomerPrice !== undefined,
    ) ?? false;

  // Determine if prices should be shown
  const shouldShowPrices =
    !hidePrices && (!showCustomerPrices || hasCustomerPrices);

  const minQuantity = getDesignMinQuantity(design);
  const [quantityForOrder, setQuantityForOrder] = useState<number>(minQuantity);
  // "See note" on a rejected design used to be an empty handler (dead
  // click); it opens the rejection note like the logo list does.
  function SeeRejectNote(e: React.MouseEvent<HTMLElement>) {
    e.stopPropagation();
    onSeeRejectNote(design);
  }

  function EditLogo(e: React.MouseEvent<HTMLElement>) {
    e.stopPropagation();
    onEditDesign(design);
  }

  function AddToCart(e: React.MouseEvent<HTMLElement>) {
    e.stopPropagation();
    onAddToCart(design, quantityForOrder);
  }

  // Flag vendorOrderWhileCustomerPendingEnabled: busy while the settle-and-
  // order chain runs (approval-start -> NAV commit -> GetDesign -> cart), so
  // a second click sends nothing. Always false on every other row.
  const [settling, setSettling] = useState(false);
  function SettleAndAddToCart(e: React.MouseEvent<HTMLElement>) {
    e.stopPropagation();
    if (settling || onSettleAndAddToCart === undefined) return;
    setSettling(true);
    Promise.resolve(onSettleAndAddToCart(design, quantityForOrder))
      .catch(() => undefined)
      .finally(() => setSettling(false));
  }

  function onDeleteClick(e?: React.MouseEvent<HTMLElement>) {
    e?.stopPropagation();
    onDeleteDesign(design);
  }

  function handleApproveClick(e: React.MouseEvent<HTMLElement>) {
    e.stopPropagation();
    onApprove(design);
  }

  function handleRejectClick(e: React.MouseEvent<HTMLElement>) {
    e.stopPropagation();
    onReject(design);
  }

  const overallState = getDesignState(design);

  const isUpdating =
    updatingDesignId === design.id && overallState === DesignState.Draft;

  // "Prices on their way" (flag productPendingEnabled): Approve / Add to
  // order are locked with a tooltip and the status cell carries the badge
  // while a placed logo's product is still owed by the API worker. Always
  // false with the flag off. "Preparing logo…" (flag cloneInstantEnabled):
  // a placed clone still preparing locks the same buttons (upload tooltip);
  // the badge in the status cell stays the price badge.
  const pricePending = isDesignActionLocked(design);
  const lockVariant = designActionLockVariant(design);

  function draftActions() {
    return (
      <>
        {design.isEditable !== false && (
          <IconButton
            key={"approve"}
            onClick={EditLogo}
            icon={<EditIcon />}
            variant="success"
          >
            {t("edit")}
          </IconButton>
        )}
        <ConfirmationGuard
          anchor={
            <IconButton icon={<TrashcanCrossIcon />} variant="error">
              {t("delete")}
            </IconButton>
          }
          text={t("designListStyle.confirmDeleteDesign")}
          onAccept={onDeleteClick}
        />
      </>
    );
  }

  function rejectedActions() {
    return (
      <>
        <IconButton
          key={"see-note"}
          onClick={SeeRejectNote}
          icon={<NoteIcon />}
        >
          {t("designListStyle.seeNote")}
        </IconButton>
        {design.isEditable !== false && (
          <IconButton
            key={"approve"}
            onClick={EditLogo}
            icon={<EditIcon />}
            variant="success"
          >
            {t("edit")}
          </IconButton>
        )}
        <ConfirmationGuard
          anchor={
            <IconButton icon={<TrashcanCrossIcon />} variant="error">
              {t("delete")}
            </IconButton>
          }
          text={t("designListStyle.confirmDeleteDesign")}
          onAccept={onDeleteClick}
        />
      </>
    );
  }

  function pendingAction() {
    if (role == undefined || role == null) {
      return;
    }

    return (
      <>
        <ProductPendingLock
          locked={pricePending}
          variant={lockVariant}
          className="inline-block"
        >
          <IconButton
            onClick={pricePending ? undefined : handleApproveClick}
            disabled={pricePending || undefined}
            icon={<FontAwesomeIcon icon={faCheck} />}
            variant="success"
          >
            {t("designListStyle.approve")}
          </IconButton>
        </ProductPendingLock>
        <IconButton
          onClick={handleRejectClick}
          icon={<FontAwesomeIcon icon={faClose} />}
          variant="error"
        >
          {t("designListStyle.reject")}
        </IconButton>
      </>
    );
  }

  // Stepper + "Add to order": the approved row's buttons (doneActions) and,
  // under flag vendorOrderWhileCustomerPendingEnabled, the dealer's settle-
  // and-order row - same DOM, only the click handler (and its busy state)
  // differs; busy = false is the approved row's exact disabled expression.
  function orderActions(
    onAdd: (e: React.MouseEvent<HTMLElement>) => void,
    busy = false,
  ) {
    return (
      <>
        <Stepper
          min={1}
          value={quantityForOrder}
          onValueChange={setQuantityForOrder}
          className="h-7"
        />
        <ProductPendingLock
          locked={pricePending}
          variant={lockVariant}
          className="inline-block"
        >
          <IconButton
            key={"add-order"}
            disabled={
              !isValidDesignQuantity(design, quantityForOrder) ||
              loadingPrices ||
              pricePending ||
              busy
            }
            onClick={pricePending ? undefined : onAdd}
            icon={<AddToCartIcon />}
            variant="success"
          >
            {loadingPrices
              ? t("designListStyle.loading")
              : t("designListStyle.addToOrder")}
          </IconButton>
        </ProductPendingLock>
      </>
    );
  }

  function doneActions() {
    const minQuantity = getDesignMinQuantity(design);
    if (role === Role.Admin) {
      return <></>;
    }

    if (cartHidden) {
      return <></>;
    }

    return orderActions(AddToCart);
  }

  // Flag vendorOrderWhileCustomerPendingEnabled (Nichlas 18/10, MUL352937):
  // the dealer's row on a design whose own vote is in while the customer's
  // is still Pending - Stepper + "Add to order" (the click settles the
  // group first, then adds to the cart) and the AWAITING row's Delete. Cart
  // hidden = Delete only, as the AWAITING row.
  function settleAndOrderActions() {
    return (
      <>
        {!cartHidden && orderActions(SettleAndAddToCart, settling)}
        {awaitingApprovalActions()}
      </>
    );
  }

  // Flag designOrderabilityGuardEnabled (#724425): the popup's rule for a
  // design under approval (design-details: cancel / delete / approve|skip -
  // no "Add to order") brought to the row. Reached for Approval and for
  // done + a pending/rejected participant (JS upload, deferred customer
  // approval) when the viewer is NOT the party whose vote is pending (that
  // party keeps Approve / Reject above): no Stepper, no "Add to order" -
  // only Delete, as the popup. Admin keeps its own branches.
  function awaitingApprovalActions() {
    return (
      <ConfirmationGuard
        anchor={
          <IconButton icon={<TrashcanCrossIcon />} variant="error">
            {t("delete")}
          </IconButton>
        }
        text={t("designListStyle.confirmDeleteDesign")}
        onAccept={onDeleteClick}
      />
    );
  }

  function getActions(state: DesignState) {
    // Check if current user has pending approval status
    const userHasPendingApproval = hasUserPendingApprovalStatus(
      design.approval,
      role as number,
    );

    // If user has pending approval status, show pending actions regardless of design state
    if (userHasPendingApproval) {
      return pendingAction();
    }

    if (overallState === DesignState.Approval && role === Role.Admin) {
      return draftActions();
    }

    // Flag vendorOrderWhileCustomerPendingEnabled: before the orderability
    // guard's Delete-only row - the dealer's own approved vote with the
    // customer's still Pending may be settled and ordered from the row.
    if (
      enviroment.vendorOrderWhileCustomerPendingEnabled &&
      onSettleAndAddToCart !== undefined &&
      vendorCanSettleAndOrder(design, role)
    ) {
      return settleAndOrderActions();
    }

    if (
      enviroment.designOrderabilityGuardEnabled &&
      role !== Role.Admin &&
      (state === DesignState.Approval || state === DesignState.Done) &&
      !isDesignOrderable(design)
    ) {
      return awaitingApprovalActions();
    }

    switch (state) {
      case DesignState.Draft:
        return draftActions();
      case DesignState.Rejected:
      case DesignState.Pending:
        return rejectedActions();
      case DesignState.Approval:
        return doneActions();
      case DesignState.Done:
        return doneActions();
    }
  }

  const actions = getActions(overallState);

  // True while (and briefly after) an HTML5 drag: suppresses the row-click
  // navigation that lives on the surrounding ListItemFrame. Browsers do not
  // normally fire click after a drag, but this guards the edge cases.
  const isDragging = useRef(false);

  const dragProps = onDragStartDesign
    ? {
        draggable: true,
        onDragStart: (e: React.DragEvent<HTMLDivElement>) => {
          isDragging.current = true;
          onDragStartDesign(design, e);
        },
        onDragEnd: () => {
          setTimeout(() => {
            isDragging.current = false;
          }, 150);
        },
        onClickCapture: (e: React.MouseEvent<HTMLDivElement>) => {
          if (isDragging.current) {
            e.preventDefault();
            e.stopPropagation();
          }
        },
      }
    : {};

  return (
    <StrucRow className={"h-[100px] items-start py-4"} {...dragProps}>
      <StrucCol
        size={columns[nameOf<Design>((x) => x.displayImage)].weight}
        className={"pr-3 rounded-md"}
      >
        {isUpdating ? (
          <div className="bg-white w-full h-[74px] flex items-center justify-center rounded-md border border-gray-100">
            <CircularProgress size={14} />
          </div>
        ) : (
          <div
            className={
              listPerf
                ? "bg-white w-full h-[74px] flex items-center justify-center rounded-md py-1"
                : "bg-white w-full max-h-full flex items-center justify-center rounded-md py-1"
            }
          >
            <DesignImage
              design={design}
              lazy={listPerf && rowIndex >= LIST_PERF_EAGER_ROWS}
              priority={listPerf && rowIndex === 0}
              reserve={listPerf ? LIST_PERF_IMAGE_BOX : undefined}
              className={
                "object-contain bg-white hover:rounded hover:border max-h-[74px] origin-left hover:scale-[3] hover:ease-out transition-all duration-150 ease-out" +
                (design.isEditable ? " hover:p-1" : "") +
                (listPerf ? " max-w-full" : "")
              }
            />
          </div>
        )}
      </StrucCol>
      <StrucCol size={columns["product.mul"].weight}>
        <div className="flex items-center">
          {/* Clarity (design 5.3): MUL = non-personal id -> unmask. */}
          <Tooltip title={design.product?.mul ?? "-"} placement={"top-start"}>
            <span
              className={
                "font-bold whitespace-nowrap overflow-x-hidden overflow-ellipsis min-w-0"
              }
              {...rowTextClickProps()}
              {...CLARITY_UNMASK}
            >
              {design.product?.mul ?? "-"}
            </span>
          </Tooltip>
          {design.product?.mul && (
            <CopyNumberButton value={design.product.mul} className="ml-1" />
          )}
        </div>
        <p>
          {design.style?.manufacturer?.name || design.navManufacturerName}{" "}
          <br />
          {design.style?.model || design.navStyleName}
        </p>
      </StrucCol>
      <StrucCol size={columns[nameOf<Design>((x) => x.title)].weight}>
        <Tooltip title={design.title} placement={"top-start"}>
          <span
            className={
              "font-bold whitespace-nowrap overflow-x-hidden overflow-ellipsis"
            }
          >
            {design.title}
          </span>
        </Tooltip>
      </StrucCol>
      {/* Vendor column (only shown for Admin users) */}
      {columns["vendor"] && (
        <StrucCol size={columns["vendor"].weight}>
          {design.vendor && <CustomerTooltip customer={design.vendor} />}
        </StrucCol>
      )}
      <StrucCol size={columns[nameOf<Design>((x) => x.customer)].weight}>
        {design.customer?.type !== "vendor" && (
          <CustomerTooltip customer={design.customer} />
        )}
      </StrucCol>
      {/* <StrucCol size={columns["priceSort"].weight}>
        {columns["priceSort"].displayValue && shouldShowPrices ? (
          loadingPrices ? (
            <div className="animate-pulse space-y-1">
              <div className="h-3 bg-gray-300 rounded w-20"></div>
              <div className="h-3 bg-gray-300 rounded w-20"></div>
              <div className="h-3 bg-gray-300 rounded w-20"></div>
              <div className="h-3 bg-gray-300 rounded w-20"></div>
            </div>
          ) : (
            <DesignPrices design={design} />
          )
        ) : (
          <span></span>
        )}
      </StrucCol> */}
      <StrucCol size={columns[nameOf<Design>((x) => x.createdAtUtc)].weight}>
        {formatDate(design.createdAtUtc)}
      </StrucCol>
      <StrucCol size={columns[nameOf<Design>((x) => x.status)].weight}>
        <DesignStatusElement design={design} />
        {pricePending && (
          <ProductPendingBadge
            pending={pricePending}
            sinceUtc={designProductPendingSinceUtc(design)}
            className="mt-1"
          />
        )}
      </StrucCol>
      <StrucCol
        size={columns["action"].weight}
        className={"items-start !justify-around space-y-2 h-full"}
      >
        {actions}
      </StrucCol>
    </StrucRow>
  );
}
